"use client";

import { useEffect } from "react";

import { detectScoreFormat } from "@/lib/upload/score-file";

export type DroppedFileKind = "midi" | "musicxml" | "pdf";

type WindowFileDropZoneProps = {
  /**
   * Called when a supported file is dropped. The kind is detected from
   * the extension at drop time (the browser hides the file name during
   * dragover for security reasons, so kind cannot be inferred earlier).
   */
  onFileDropped: (file: File, kind: DroppedFileKind) => void;
  /**
   * Called when an unsupported file is dropped, caller flashes the error
   * overlay. The file comes with it because not every refusal says the same
   * thing: a MuseScore project has an export to point at, an .mp3 has
   * nothing.
   */
  onUnsupportedFile?: (file: File) => void;
  /**
   * Notifies the host that a drag is in flight. Used to render the
   * `DropFrame` around the canvas. Always called with `null` when the
   * drag ends; `"midi"` is the optimistic guess otherwise (we cannot
   * read file extensions during dragover).
   */
  onDragChange?: (kind: DroppedFileKind | null) => void;
  /**
   * Whether the drop zone is *armed*. When `false`, drag events are
   * ignored, used to disable the listener once a piece is loaded.
   */
  enabled?: boolean;
};

/**
 * The window-level catcher stays narrower than the modal's own drop zone: a
 * single file, and only the families that make sense dropped onto a piano
 * roll. Loose photos still go through the modal, where the user can add the
 * other pages.
 */
function classifyFile(file: File): DroppedFileKind | null {
  const format = detectScoreFormat(file);

  if (format !== null) {
    return format;
  }

  if (file.name.toLowerCase().endsWith(".pdf") || file.type === "application/pdf") {
    return "pdf";
  }

  return null;
}

/**
 * Window-wide drag-and-drop catcher for MIDI / MusicXML / PDF files.
 *
 * Renders nothing, the visible UI (dashed `DropFrame`, "Lâche pour
 * importer" copy) lives inside `StudioDropOverlay`. This component is a
 * pure listener that translates window-level drag events into props the
 * overlay can react to.
 *
 * The dragover phase optimistically reports `"midi"` because the browser
 * security model prevents reading the file extension before drop. The
 * drop handler does the real classification: a supported family goes to
 * `onFileDropped`, anything else fires `onUnsupportedFile`.
 */
export function WindowFileDropZone({
  onFileDropped,
  onUnsupportedFile,
  onDragChange,
  enabled = true,
}: WindowFileDropZoneProps) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Counter-based guard: nested dragenter/dragleave events fire alternately
    // when the cursor crosses child elements; counting balances them out.
    let dragCounter = 0;

    const hasFiles = (e: DragEvent) => {
      const types = e.dataTransfer?.types;

      if (!types) {
        return false;
      }

      for (let i = 0; i < types.length; i++) {
        if (types[i] === "Files") {
          return true;
        }
      }

      return false;
    };

    const handleDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) {
        return;
      }

      e.preventDefault();
      dragCounter += 1;

      if (dragCounter === 1) {
        // Optimistic kind, corrected at drop time.
        onDragChange?.("midi");
      }
    };

    const handleDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) {
        return;
      }

      // dragover must be cancelled or the browser refuses the drop.
      e.preventDefault();
    };

    const handleDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) {
        return;
      }

      e.preventDefault();
      dragCounter = Math.max(0, dragCounter - 1);

      if (dragCounter === 0) {
        onDragChange?.(null);
      }
    };

    const handleDrop = (e: DragEvent) => {
      if (!hasFiles(e)) {
        return;
      }

      e.preventDefault();
      dragCounter = 0;
      onDragChange?.(null);

      const file = e.dataTransfer?.files[0];

      if (!file) {
        return;
      }

      const kind = classifyFile(file);

      if (kind === null) {
        onUnsupportedFile?.(file);

        return;
      }

      onFileDropped(file, kind);
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, [enabled, onFileDropped, onUnsupportedFile, onDragChange]);

  return null;
}
